import {
  CreatePreProposalInputDTO,
  ProposalAttendantListResponseDTO,
  ProposalDailySummaryResponseDTO,
  ProposalProductListResponseDTO,
  RequiredDocumentsListResponseDTO,
  ShopkeeperResponseSchemaDTO,
  StoreDetailResponseSchemaDTO,
  UpdatePreProposalInputDTO,
  UploadDocumentInputDTO,
  UploadDocumentOutputDTO,
  FetchPointsInputDTO,
  FetchPointsResponseDTO,
  FetchAttendantsInputDTO,
  CreateProposalInputDTO,
  ConsultProposalsGroupedByDateResponseDTO,
  ConsultProposalResponseDTO,
  ConsultProposalSituationResponseDTO,
  UpdateProposalInputDTO,
  ProposalDetailsResponseDTO,
  ProposalDocumentsListResponseDTO,
  ProposalAnalysisReasonDTO,
  LastCaptureResponseDTO,
  BiometryLastProcessResultResponseDTO
} from "@repo/core/services/entities/Proposals";

import { AxiosInstance, AxiosResponse } from "axios";
import useAuthStore from "@repo/core/stores/auth";
import { sanitizeParentNameForApi } from "@repo/core/utils/parentName.utils";

const resourcePrefix = "/Proposta";
const resourcePrefixNewApi = "/proposals";
const resourceProposalMobile = "/PropostaMobile";

const sanitizeFatherNameForApiPayload = <
  T extends { strNomePai?: string | null }
>(
  data: T
): T => {
  const { strNomePai, ...payload } = data;
  const sanitizedFatherName = sanitizeParentNameForApi(strNomePai?.trim());

  return {
    ...payload,
    ...(sanitizedFatherName && { strNomePai: sanitizedFatherName })
  } as T;
};

export class ProposalsResource {
  apiProvider: AxiosInstance;

  constructor(instance: AxiosInstance) {
    this.apiProvider = instance;
  }

  private buildUpdatePreProposalPayload(
    data: UpdatePreProposalInputDTO
  ): UpdatePreProposalInputDTO {
    const strDigitador = useAuthStore
      .getState()
      .user?.tokenData?.strUsuario?.trim();

    if (!strDigitador) {
      throw new Error("Digitador não encontrado no token do usuário");
    }

    return {
      ...sanitizeFatherNameForApiPayload(data),
      strDigitador
    };
  }

  async getDailySummary(): Promise<
    AxiosResponse<ProposalDailySummaryResponseDTO>
  > {
    return this.apiProvider.get(`${resourcePrefixNewApi}/daily-summary`);
  }

  async fetchShopkeepers(
    tokenData: string
  ): Promise<AxiosResponse<ShopkeeperResponseSchemaDTO>> {
    return this.apiProvider.get(`${resourcePrefix}/ConsultaLojistas`, {
      timeout: 30_000,
      headers: {
        Authorization: `Bearer ${tokenData}`
      }
    });
  }

  async fetchStores(
    tokenData: string,
    shopkeeperId: string
  ): Promise<AxiosResponse<StoreDetailResponseSchemaDTO>> {
    return this.apiProvider.get(
      `${resourcePrefix}/ConsultaLojas?strLojista=${shopkeeperId}`,
      {
        timeout: 30_000,
        headers: {
          Authorization: `Bearer ${tokenData}`
        }
      }
    );
  }

  async editPreProposal(
    data: UpdatePreProposalInputDTO
  ): Promise<AxiosResponse> {
    return this.apiProvider.post(
      `${resourcePrefix}/AlterarPre`,
      this.buildUpdatePreProposalPayload(data)
    );
  }

  async fetchInvoiceExpirationDates(
    strProduto: string
  ): Promise<AxiosResponse> {
    return this.apiProvider.get(
      `${resourcePrefix}/ConsultaVencimentos?strProduto=${strProduto}`
    );
  }

  async fetchPoints(
    params: FetchPointsInputDTO
  ): Promise<AxiosResponse<FetchPointsResponseDTO>> {
    return this.apiProvider.get(`${resourcePrefix}/ConsultaPontosAtendimento`, {
      params
    });
  }

  async fetchAttendants(
    params: FetchAttendantsInputDTO
  ): Promise<AxiosResponse<ProposalAttendantListResponseDTO[]>> {
    return this.apiProvider.get(`${resourcePrefix}/ConsultaAtendentes`, {
      params
    });
  }

  async getProposal(
    lngProposta: string
  ): Promise<AxiosResponse<ProposalDetailsResponseDTO>> {
    return this.apiProvider.get(`${resourcePrefix}/Consultar/${lngProposta}`);
  }

  async getProposalSituation(
    lngProposta: string
  ): Promise<AxiosResponse<ConsultProposalSituationResponseDTO>> {
    return this.apiProvider.get(
      `${resourcePrefix}/ConsultarSituacaoProposta/${lngProposta}`
    );
  }

  async getAnalysisReasons(
    proposalId: string
  ): Promise<AxiosResponse<ProposalAnalysisReasonDTO[]>> {
    return this.apiProvider.get(
      `${resourcePrefixNewApi}/${proposalId}/analysis-reasons`
    );
  }

  async validatePhoneNumber(
    ddd: string,
    phoneNumber: string,
    strCliente?: string
  ): Promise<AxiosResponse<boolean>> {
    return this.apiProvider.get(
      /* The 1 value on the end of strCliente is needed to avoid false positives on the validation */
      `${resourcePrefix}/ValidarTelefone?strDDD=${ddd}&strTefone=${phoneNumber}&strCliente=${strCliente}1`
    );
  }

  async getProposalDetails(
    lngProposta: string
  ): Promise<AxiosResponse<ConsultProposalResponseDTO>> {
    return this.apiProvider.get(
      `${resourceProposalMobile}/ConsultarProposta?lngProposta=${lngProposta}`
    );
  }

  async validateRequiredDocuments(
    strProduto: string
  ): Promise<AxiosResponse<RequiredDocumentsListResponseDTO>> {
    return this.apiProvider.get(
      `${resourcePrefix}/ConsultarDocumentosObrigatorios/${strProduto}`
    );
  }

  async getProducts(): Promise<
    AxiosResponse<ProposalProductListResponseDTO[]>
  > {
    return this.apiProvider.get(`${resourcePrefix}/ConsultaProdutos`);
  }

  async createProposal(
    data: CreateProposalInputDTO
  ): Promise<AxiosResponse<{ lngProposta: number }>> {
    return this.apiProvider.post(`${resourcePrefix}/Incluir`, data);
  }

  async createPreProposal(
    data: CreatePreProposalInputDTO
  ): Promise<AxiosResponse<number>> {
    return this.apiProvider.post(`${resourcePrefix}/IncluirPre`, data);
  }

  async updatePreProposal(
    data: UpdatePreProposalInputDTO
  ): Promise<AxiosResponse<number>> {
    return this.apiProvider.post(
      `${resourcePrefix}/AlterarPre`,
      this.buildUpdatePreProposalPayload(data)
    );
  }

  async uploadDocument(
    lngProposta: string,
    data: UploadDocumentInputDTO
  ): Promise<AxiosResponse<UploadDocumentOutputDTO>> {
    return this.apiProvider.post(
      `${resourcePrefix}/UploadDocumento/${lngProposta}`,
      data,
      {
        maxContentLength: Infinity,
        maxBodyLength: Infinity
      }
    );
  }

  async consultProposals(): Promise<
    AxiosResponse<ConsultProposalsGroupedByDateResponseDTO>
  > {
    return this.apiProvider.get(`${resourcePrefix}/Consultar`);
  }

  async editProposal(
    data: UpdateProposalInputDTO
  ): Promise<AxiosResponse<{ lngProposta: number }>> {
    const { user } = useAuthStore.getState();
    const loggedInUser = user?.tokenData?.strUsuario?.trim();
    const proposalData = sanitizeFatherNameForApiPayload(
      Object.fromEntries(
        Object.entries(data).filter(
          ([key]) => key.toLowerCase() !== "strufemidocident"
        )
      )
    ) as UpdateProposalInputDTO;

    // Always send the logged-in user as digitador on Proposta/Alterar
    return this.apiProvider.post(`${resourcePrefix}/Alterar`, {
      ...proposalData,
      strDigitador: loggedInUser || proposalData.strDigitador || ""
    });
  }

  async sendToManualAnalysis({
    lngProposta,
    strObservacao
  }: {
    lngProposta: string;
    strObservacao: string;
  }): Promise<AxiosResponse> {
    return this.apiProvider.post(
      `${resourcePrefix}/EnviarAnaliseManual`,
      undefined,
      {
        params: {
          lngProposta,
          strObservacao
        }
      }
    );
  }

  async getDocumentsList(
    lngProposta: string
  ): Promise<AxiosResponse<ProposalDocumentsListResponseDTO>> {
    return this.apiProvider.get(`/mobilepropostas/Propostas/ListaDocumentos`, {
      params: {
        lngProposta
      }
    });
  }

  async effectuateProposal(lngProposta: string): Promise<AxiosResponse> {
    return this.apiProvider.post(`${resourcePrefix}/Efetivar/${lngProposta}`);
  }

  async effectuateProposalWithBody(
    lngProposta: string
  ): Promise<AxiosResponse> {
    return this.apiProvider.post(`${resourcePrefix}/Efetivar`, {
      lngProposta
    });
  }

  async getLastCapture(
    lngProposta: string,
    options?: { limiteMinutos?: number }
  ): Promise<AxiosResponse<LastCaptureResponseDTO | Record<string, never>>> {
    const params =
      options?.limiteMinutos != null
        ? { limite_minutos: options.limiteMinutos }
        : undefined;
    return this.apiProvider.get(
      `${resourcePrefixNewApi}/${lngProposta}/last-capture`,
      { params }
    );
  }

  /**
   * Latest Unico process id for "Resultado do Processo" for the client (Nest API).
   * Uses the same strCliente suffix as ExisteRegistroUnico (trailing "1") for ERP consistency.
   */
  async getLastBiometryProcessResult(
    strCliente: string
  ): Promise<AxiosResponse<BiometryLastProcessResultResponseDTO>> {
    const strClienteParam = `${strCliente}1`;
    return this.apiProvider.get(
      `${resourcePrefixNewApi}/biometry/last-result-process-id`,
      {
        params: { strCliente: strClienteParam }
      }
    );
  }
}
